import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { createTask } from '../services/firestore';
import TaskListViewImproved from '../../TaskListViewImproved';
import DashboardView from '../components/DashboardView';
import VoiceAssistant from '../components/VoiceAssistant';
import KanbanBoard from '../components/KanbanBoardNew';
import CalendarView from '../components/CalendarView';
import GanttView from '../components/GanttView';
import WorkloadView from '../components/WorkloadView';
import WeeklyView from '../components/WeeklyView';
import CoursesView from '../components/CoursesView';
import NotesView from '../components/NotesView';
import useKeyboardShortcuts, { SHORTCUTS } from '../hooks/useKeyboardShortcuts';
import useProjectRole from '../hooks/useProjectRole';

const ListPage = () => {
    const { listId } = useParams();
    const { user } = useAuth();
    const {
        setActiveView,
        activeView,
        setSelectedListId,
        tasks,
        filteredTasks,
        sections,
        users,
        selectedProjectId,
        selectedFolderId,
        setSelectedProjectId,
        setSelectedFolderId,
        setSelectedTaskId,
        setShowTaskDrawer
    } = useTask();

    const { can, isViewer } = useProjectRole(user?.uid, selectedProjectId);

    useEffect(() => {
        setActiveView('lista');
        if (listId) {
            setSelectedListId(listId);

            // If project or folder is missing (direct navigation), try to restore them
            if ((!selectedProjectId || !selectedFolderId) && user?.uid) {
                const { getListDetails } = require('../services/firestore');

                getListDetails(user.uid, listId).then(listData => {
                    if (listData) {
                        if (listData.projectId) setSelectedProjectId(listData.projectId);
                        if (listData.folderId) setSelectedFolderId(listData.folderId);
                    }
                }).catch(err => console.error("Could not recover context for list", err));
            }
        }
    }, [listId, setActiveView, setSelectedListId, selectedProjectId, selectedFolderId, user?.uid, setSelectedProjectId, setSelectedFolderId]);

    const handleCreateTask = async (taskData) => {
        if (!user?.uid || !listId) {
            console.error("User not logged in or listId not available to create task.");
            return;
        }
        try {
            await createTask(user.uid, selectedProjectId, selectedFolderId, listId, taskData);
            // Optionally, refresh tasks or show a success message
        } catch (error) {
            console.error("Error creating task from VoiceAssistant:", error);
        }
    };

    const searchInputRef = useRef(null);

    const { showHelp, setShowHelp } = useKeyboardShortcuts({
        onNewTask: () => {
            // Trigger the add task row in list view by simulating click on add button
            const addBtn = document.querySelector('[data-action="add-task"]');
            if (addBtn) addBtn.click();
        },
        onSearch: () => {
            const searchInput = document.querySelector('[data-action="search-input"]');
            if (searchInput) { searchInput.focus(); searchInput.select(); }
        },
        onToggleView: (view) => setActiveView(view),
        onEscape: () => {
            setSelectedTaskId(null);
            setShowTaskDrawer(false);
        },
        onDelete: () => {
            const deleteBtn = document.querySelector('[data-action="bulk-delete"]');
            if (deleteBtn) deleteBtn.click();
        },
        onSelectAll: () => {
            const selectAllBtn = document.querySelector('[data-action="select-all"]');
            if (selectAllBtn) selectAllBtn.click();
        },
    });

    console.log('Active View in ListPage:', activeView);

    return (
        <div className="flex-1 flex flex-col h-full">
            {/* Keyboard Shortcuts Help Modal */}
            {showHelp && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowHelp(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-96 max-w-[90vw] overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-100">Atajos de teclado</h3>
                            <button onClick={() => setShowHelp(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-lg leading-none">&times;</button>
                        </div>
                        <div className="px-5 py-3 space-y-1">
                            {SHORTCUTS.map((s, i) => (
                                <div key={i} className="flex items-center justify-between py-2">
                                    <span className="text-xs text-gray-600 dark:text-gray-300">{s.description}</span>
                                    <div className="flex gap-1">
                                        {s.keys.map((k, j) => (
                                            <kbd key={j} className="px-2 py-0.5 text-[10px] font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-600">{k}</kbd>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-[10px] text-gray-400 text-center">Presiona <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[9px] font-mono">?</kbd> para cerrar</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex-1 overflow-y-auto">
                {activeView === 'panel' ? (
                    <DashboardView tasks={filteredTasks} sections={sections} users={users} />
                ) : activeView === 'tablero' ? (
                    <div className="h-full overflow-auto">
                        <KanbanBoard />
                    </div>
                ) : activeView === 'gantt' ? (
                    <GanttView />
                ) : activeView === 'calendario' ? (
                    <CalendarView tasks={filteredTasks} users={users} />
                ) : activeView === 'carga' ? (
                    <WorkloadView />
                ) : activeView === 'semanal' ? (
                    <WeeklyView />
                ) : activeView === 'cursos' ? (
                    <CoursesView />
                ) : activeView === 'notas' ? (
                    <NotesView />
                ) : (
                    <TaskListViewImproved
                        tasks={filteredTasks}
                        selectedProjectId={selectedProjectId}
                        selectedFolderId={selectedFolderId}
                        selectedListId={listId}
                        onTaskClick={setSelectedTaskId}
                        setShowTaskDrawer={setShowTaskDrawer}
                        setSelectedTaskId={setSelectedTaskId}
                        viewType="list"
                        readOnly={isViewer}
                    />
                )}
            </div>
            <VoiceAssistant
                sections={sections}
                users={users}
                onAddTask={handleCreateTask}
            />
        </div>
    );
};

export default ListPage;
